import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@parcel/ui';
import { AppLogo } from '@parcel/ui/app-logo';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Building2, MoreHorizontal } from 'lucide-react';
import * as React from 'react';
import { accountWebsite } from '../../lib/account-website.js';
import { formatHeadcount, formatRevenue } from '../../lib/accounts.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { logoUrl } from '../../lib/logo.js';
import { ACCOUNT_TYPE_TO_KIND, type AccountRow, KIND_META } from '../../lib/mock-accounts.js';
import type { Score } from '../../lib/scores.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import { ACCOUNTS_DEFAULT_HIDDEN } from '../../lib/table-views.js';
import { BadgeOverflowList } from '../data-table/badge-overflow.js';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE,
  type PinnedColumn,
} from '../data-table/data-table-layout.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';
import { scoreColumn } from '../scoring/score-column.js';

export const ACCOUNTS_PINNED_COLUMNS: PinnedColumn[] = [
  { id: 'select', left: 0, width: 52 },
  { id: 'name', left: 52, width: 316, last: true },
];

export const ACCOUNT_TABLE_COLUMN_ORDER = [
  'select',
  'name',
  'icpScore',
  'projectsCount',
  'headcount',
  'industry',
  'revenue',
  'foundedYear',
  'kind',
  'lastActivityAt',
  'actions',
] as const;

export const ACCOUNTS_DEFAULT_SORTING: SortingState = [{ id: 'lastActivityAt', desc: true }];

export interface AccountsTableOptions {
  browseMode: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** The workspace's ICP Score, for its bucket labels. */
  icpScore?: Score;
}

export function createAccountColumns(
  options: Pick<AccountsTableOptions, 'icpScore'> & { browseMode?: boolean } = {},
): ColumnDef<AccountRow>[] {
  const browseMode = options.browseMode ?? false;
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={
              browseMode
                ? table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && 'indeterminate')
                : table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              browseMode
                ? table.toggleAllRowsSelected(Boolean(value))
                : table.toggleAllPageRowsSelected(Boolean(value))
            }
            aria-label={browseMode ? 'Select all loaded accounts' : 'Select all'}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${row.original.name}`}
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 52,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Account" />,
      cell: ({ row }) => {
        const website = accountWebsite(row.original);
        const linkedin = normalizeExternalUrl(row.original.linkedinUrl ?? null);
        return (
          <div className="flex min-w-0 items-center gap-3">
            <EntityAvatar
              src={logoUrl(website)}
              alt={row.original.name}
              fallbackIcon={Building2}
              size="sm"
              href={normalizeExternalUrl(website)}
            />
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
              {row.original.name}
            </span>
            {linkedin ? (
              <a
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                aria-label={`${row.original.name} on LinkedIn`}
                className="inline-flex shrink-0 opacity-50 grayscale transition hover:opacity-100 hover:grayscale-0"
              >
                <AppLogo name="linkedin" size={14} title="LinkedIn" />
              </a>
            ) : null}
          </div>
        );
      },
      size: 316,
    },
    scoreColumn<AccountRow>('account', options.icpScore),
    {
      accessorKey: 'projectsCount',
      id: 'projectsCount',
      header: 'Projects',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-sm tabular-nums">
          {row.original.projectsCount.toLocaleString('en-US')}
        </div>
      ),
      size: 120,
    },
    {
      id: 'headcount',
      header: 'Employees',
      enableSorting: false,
      cell: ({ row }) => <FirmFact value={formatHeadcount(row.original.firmographics)} />,
      size: 130,
    },
    {
      id: 'industry',
      header: 'Industry',
      enableSorting: false,
      cell: ({ row }) => <FirmFact value={row.original.firmographics?.industry ?? null} />,
      size: 200,
    },
    {
      id: 'revenue',
      header: 'Revenue',
      enableSorting: false,
      cell: ({ row }) => <FirmFact value={formatRevenue(row.original.firmographics)} />,
      size: 150,
    },
    {
      id: 'foundedYear',
      header: 'Founded',
      enableSorting: false,
      cell: ({ row }) => (
        <FirmFact value={row.original.firmographics?.founded_year?.toString() ?? null} />
      ),
      size: 110,
    },
    {
      accessorKey: 'kind',
      header: 'Account Type',
      cell: ({ row }) => {
        const types = row.original.account_types.filter((t) => t !== 'other');
        return (
          <BadgeOverflowList
            items={types.map((t) => {
              const kind = KIND_META[ACCOUNT_TYPE_TO_KIND[t] ?? 'unknown'];
              return { key: t, label: kind.label, variant: kind.variant };
            })}
          />
        );
      },
      enableSorting: false,
      size: 240,
    },
    {
      id: 'lastActivityAt',
      accessorFn: accountLastActivitySortValue,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Activity"
          tooltip="Most recent subscribed signal for this workspace."
        />
      ),
      cell: ({ row }) => (
        <div className="text-sm">{formatSignalCalendarDate(row.original.lastActivityAt)}</div>
      ),
      size: 180,
    },
    {
      id: 'actions',
      enableHiding: false,
      // The 32px button and the cell padding overflowed 56px, which drew a second ellipsis.
      size: 64,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open row actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem>Open account</DropdownMenuItem>
            <DropdownMenuItem>View projects</DropdownMenuItem>
            <DropdownMenuItem>View contacts</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                row.toggleSelected(true);
              }}
            >
              Add to View
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}

function FirmFact({ value }: { value: string | null }) {
  return value ? (
    <div className="truncate text-sm text-foreground">{value}</div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

export function getAccountTableColumnIds() {
  return [...ACCOUNT_TABLE_COLUMN_ORDER];
}

export function accountLastActivitySortValue(account: Pick<AccountRow, 'lastActivityAt'>): number {
  return account.lastActivityAt ? Date.parse(account.lastActivityAt) : Number.NEGATIVE_INFINITY;
}

export function useAccountsTable(accounts: AccountRow[], options: AccountsTableOptions) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => ({
    ...ACCOUNTS_DEFAULT_HIDDEN,
  }));
  const [rowSelection, setRowSelection] = React.useState({});
  const { browseMode, icpScore } = options;
  const columns = React.useMemo(
    () => createAccountColumns({ browseMode, icpScore }),
    [browseMode, icpScore],
  );

  const table = useReactTable({
    data: accounts,
    columns,
    // Key rows by account id (not array index) so that when the list grows or
    // re-sorts as pages stream in, existing rows reorder instead of re-mounting
    // (a re-mount drops row state).
    getRowId: (row) => row.id,
    state: {
      sorting: options.sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE,
      },
    },
    enableRowSelection: true,
    onSortingChange: options.onSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: options.browseMode,
    getPaginationRowModel: options.browseMode ? undefined : getPaginationRowModel(),
    getSortedRowModel: options.browseMode ? undefined : getSortedRowModel(),
  });

  return { table };
}
